/* WeQuote Dev Board — THE shared card pop-up (PRD §6: same pop-up from every
 * view, read-first with in-place Edit). Ports detailReadHtml (modal ctx,
 * two-column), detailEditHtml/editPanelHtml, and the sub-modals: Add request,
 * Chase log, Chase history, Archive-with-reason, Mark-as-Live confirm.
 * Needs-Review resolve bar + suggest chip/triage actions land in later phases. */
import React, { useState } from 'react'
import { useDevBoard } from '../DevBoardProvider'
import * as M from '../core/mutations'
import * as L2 from '../core/logic'
import type { Feature } from '../core/types'
import { DEVCOLS, DL, EL, PL, CUST_OPTS, TIME_OPTS, MODULE_OPTS, POOLCOLS, isProgress, pastDesign, timeLabel, statusLabels, calcEffort as calcEffortLocal } from '../core/constants'
import { fmtDate, todayISO, rankCls, fmtTs as fmtTsLocal } from '../core/dates'
import { activePhase, statusDisplay, statusPillCls, custLabel, custDescOf, reqCount, rankMap, inPipelineF, reqGroups } from '../core/logic'
import { TitleWithBug, PhaseProgress, ChecklistBlock, DetailTabs, SourceLine, LinksEditor } from './parts'
import * as S from '../core/suggest'
import * as R from '../core/review'
import { ReviewResolveBar } from './ReviewView'
import { CustomerPicker } from './CustomerPicker'
import { StageEditor } from './StageEditor'
import { ScheduleModalBody } from './ScheduleModal'
import { useQuickEdit } from './QuickEdit'
import { IcPlus, IcFlag, IcCheck, IcPin, IcPlay, IcPause, IcPencil, IcTrash, IcLink, IcArchive, IcCalendar } from '../icons'

/* ── read view (detailReadHtml, ctx='modal') ── */
export const ReadView: React.FC<{ feat: Feature; inline?: boolean; onToggleEdit?: () => void; onClose?: () => void }> = ({ feat, inline, onToggleEdit, onClose }) => {
  const { cards, mutate, toggleDetailEdit: ctxToggle, closeCardModal: ctxClose, openSubModal } = useDevBoard()
  const toggleDetailEdit = onToggleEdit || ctxToggle
  const closeCardModal = onClose || ctxClose
  const ph = activePhase(feat)
  const rank = rankMap(cards)[feat.id]
  const qe = useQuickEdit(feat) // click a field to edit it in place
  const cn = feat.customerName && ('' + feat.customerName).trim() ? ('' + feat.customerName).trim() : ''
  const cd = custDescOf(feat)
  const links = feat.links || []
  return (
    <>
      <div className="dv-d-top">
        <div className="dv-d-heading">
          <div className="dv-d-titlerow">{rank ? <span className={'pnum-badge ' + rankCls(rank) + ' qe-click'} title="Priority position — click to move" onClick={qe.start('pos')}>{rank}</span> : null}<h2 className="dv-d-title"><TitleWithBug feat={feat} /></h2></div>
        </div>
        <div className="dv-d-actions">
          {feat.status === 'needsreview' && <span className="rv-actions-top"><ReviewResolveBar id={feat.id} top /></span>}
          <button className="dv-quick-btn req" title="Add a request" onClick={() => openSubModal({ kind: 'addRequest', id: feat.id })}><IcPlus size={12} /> Request</button>
          {inPipelineF(feat) && <button className="dv-quick-btn sched" title="Schedule — pick a dev and start date" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'schedule', id: feat.id }) }}><IcCalendar size={12} /> Schedule</button>}
          {S.isTriage(feat) && (feat.blocker
            ? <button className="dv-quick-btn blk on" onClick={(e) => { e.stopPropagation(); mutate((fs) => S.markBlocker(fs, feat.id)) }}><IcFlag size={12} /> Blocker <IcCheck size={12} /></button>
            : <button className="dv-quick-btn blk" onClick={(e) => { e.stopPropagation(); mutate((fs) => S.markBlocker(fs, feat.id)) }}><IcFlag size={12} /> Mark as blocker</button>)}
          {S.isTriage(feat) && !feat.blocker && (feat.triagePinned
            ? <button className="dv-quick-btn" onClick={(e) => { e.stopPropagation(); mutate((fs) => S.unpinCard(fs, feat.id)) }}><IcPin size={12} /> Unpin</button>
            : <button className="dv-quick-btn" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'pinConfirm', id: feat.id, pos: S.pinDefaultPos(feat.id, cards) }) }}><IcPin size={12} /> Pin position</button>)}
          {feat.status === 'onhold'
            ? <button className="dv-quick-btn resume" onClick={(e) => { e.stopPropagation(); mutate((fs) => M.resumeCard(fs, feat.id)) }}><IcPlay size={12} /> Resume</button>
            : pastDesign(feat.status)
              ? <button className="dv-quick-btn hold" onClick={(e) => { e.stopPropagation(); mutate((fs) => M.placeOnHold(fs, feat.id)) }}><IcPause size={12} /> Place on Hold</button>
              : null}
          {feat.status !== 'live' && <button className="dv-edit-btn dv-icon dv-live-btn" title="Mark as Live" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'markLive', id: feat.id }) }}><IcCheck size={13} /></button>}
          <button className="dv-edit-btn dv-icon" title="Edit" onClick={toggleDetailEdit}><IcPencil size={13} /></button>
          <button className="dv-quick-btn arch dv-icon" title="Archive" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'archive', id: feat.id }) }}><IcTrash size={13} /></button>
        </div>
        <button className="dv-modal-close" title="Close" onClick={closeCardModal}>×</button>
      </div>
      {(cn || cd) && (
        <div className="dv-d-subline">
          {cn && <div className="dv-d-cust">Customer-facing: <strong>{cn}</strong></div>}
          {cd && <div className="dv-d-custdesc">{cd}</div>}
        </div>
      )}
      <div className={inline ? '' : 'dv-cols'}>
        <div className={inline ? '' : 'dv-col dv-col-a'}>
          <div className="dv-d-pills">
            <span className={'badge priority-' + feat.pri + ' qe-click'} title="Click to change priority" onClick={qe.start('pri')}>{PL[feat.pri]}</span>
            <span className={'mod-chip' + (feat.module ? '' : ' none') + ' qe-click'} title="Module — click to change" onClick={qe.start('module')}>{feat.module || 'No module'}</span>
            <span className={'status-pill ' + statusPillCls(feat) + ' qe-click'} title="Click to change status" onClick={qe.start('status')}>{statusDisplay(feat)}</span>
            {feat.time && (feat.multiStage
              ? <span className={'badge effort-' + feat.eff} title="Effort derives from the stage estimates">{EL[feat.eff]}</span>
              : <span className={'badge effort-' + feat.eff + ' qe-click'} title="Click to change the estimate" onClick={qe.start('time')}>{EL[feat.eff]}</span>)}
            {S.isTriage(feat) && (
              <span className="sug-chip" onClick={(e) => { e.stopPropagation(); openSubModal({ kind: 'suggestInfo', id: feat.id }) }} title="Click to see where this would rank and why">Suggested {S.suggestScore(feat)} ⓘ</span>
            )}
            {feat.blocker && <span className="sug-blocker" title="Blocker — pinned to the top"><IcFlag size={11} /> Blocker</span>}
            {feat.triagePinned && <span className="sug-pin" title="Pinned position"><IcPin size={11} /></span>}
          </div>
          {feat.status !== 'live' && feat.status !== 'archived' && (
            <div className="dv-d-designreq">
              <span className="dv-d-k">Design</span>
              <label className="rq-design"><input type="checkbox" checked={!!feat.requiresDesign} onChange={(e) => mutate((fs) => M.setRequiresDesign(fs, feat.id, e.target.checked))} /> Design required</label>
              <label className="rq-design" title="Flags this card as a bug (separate from its module)"><input type="checkbox" checked={!!feat.isBug} onChange={(e) => mutate((fs) => M.upd(fs, feat.id, 'isBug', e.target.checked))} /> Bug</label>
            </div>
          )}
          <div className="dv-d-meta">
            <div className="dv-d-cell"><span className="dv-d-k">Est Dev Time</span>{feat.multiStage
              ? <span className="dv-d-v" title="Derived from the stages — edit the stage estimates">{feat.time ? timeLabel(feat.time) : 'TBD'} <em className="dv-d-derived">Σ stages</em></span>
              : <span className="dv-d-v qe-text" title="Click to change" onClick={qe.start('time')}>{feat.time ? timeLabel(feat.time) : 'TBD'}</span>}</div>
            <div className="dv-d-cell"><span className="dv-d-k">Requests</span><span className="dv-d-v">{reqCount(feat)}</span></div>
            <div className="dv-d-cell"><span className="dv-d-k">Customer(s)</span><span className="dv-d-v qe-text" title="Click to edit customers" onClick={qe.start('cust')}>{custLabel(feat)}</span></div>
            <div className="dv-d-cell"><span className="dv-d-k">Assigned Dev</span><span className="dv-d-v qe-text" title="Click to change the assigned dev" onClick={qe.start('dev')}>{feat.dev && DL[feat.dev] ? DL[feat.dev] : 'Unassigned'}</span></div>
          </div>
          <PhaseProgress feat={feat} onDeadlineClick={qe.start('deadline')} onStartClick={qe.start('start')} onProgressClick={qe.start('progress')} />
          {qe.editor}
        </div>
        <div className={inline ? '' : 'dv-col dv-col-b'}>
          <div className="dv-d-sec">
            {inPipelineF(feat) && <StageEditor feat={feat} />}
            <ChecklistBlock feat={feat} />
            {links.length > 0 && (
              <div className="dv-d-block"><span className="dv-d-k">Attachments / Links</span>
                <div className="dv-d-links">{links.map((l, i) => <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" className="dv-lnk-btn"><IcLink size={12} /> {l.label || l.url}</a>)}</div>
              </div>
            )}
            {ph === 'dev' && feat.pnotes && <div className="dv-d-block"><span className="dv-d-k">Progress notes</span><div className="dv-d-text">{feat.pnotes}</div></div>}
            {ph === 'design' && feat.designNotes && <div className="dv-d-block"><span className="dv-d-k">Design notes</span><div className="dv-d-text">{feat.designNotes}</div></div>}
            <DetailTabs feat={feat} />
            {(feat.notifications || []).length > 0 && (
              <div className="dv-d-block">
                <span className="dv-d-k">Emails sent</span>
                <div className="notif-log">
                  {(feat.notifications || []).slice(-5).reverse().map((n, i) => (
                    <div key={i} className="notif-row">{n.event === 'review' ? 'Needs Review alert' : n.event === 'priority' ? 'Priority list (everyone)' : n.event === 'devcolumn' ? 'Dev started (Natalie)' : n.event === 'assigned' ? 'Assigned (to the dev)' : n.event === 'live' ? 'Live (everyone)' : n.event} → {n.to} · {fmtTsLocal(n.ts)}</div>
                  ))}
                </div>
              </div>
            )}
            <SourceLine feat={feat} />
          </div>
        </div>
      </div>
    </>
  )
}

/* ── edit panel (editPanelHtml, verbatim field set + stage visibility) ── */
export const EditPanel: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { cards, mutate, openSubModal } = useDevBoard()
  const prog = isProgress(feat.status)
  const myRank = rankMap(cards)[feat.id]
  const orderedPipe = cards.filter(inPipelineF).sort((a, b) => L2.sectionRank(a) - L2.sectionRank(b) || a.po - b.po)
  const bLo = orderedPipe.findIndex((x) => L2.sectionRank(x) === L2.sectionRank(feat)) + 1
  let bHi = bLo
  while (bHi < orderedPipe.length && L2.sectionRank(orderedPipe[bHi]) === L2.sectionRank(feat)) bHi++
  const bandPositions = Array.from({ length: Math.max(bHi - bLo + 1, 1) }, (_, i) => bLo + i)
  const total = cards.filter(inPipelineF).length
  const isDesignStage = feat.status === 'design' || feat.status === 'designcomplete'
  const SL = statusLabels()
  const set = (field: keyof Feature, val: any) => mutate((fs) => M.upd(fs, feat.id, field, val))
  const custVal = CUST_OPTS.some((o) => o.v === (feat.cust || '')) ? (feat.cust || '') : ''
  const timeOpts = TIME_OPTS.some((o) => o.val === feat.time) ? TIME_OPTS : [{ val: feat.time, label: timeLabel(feat.time) }, ...TIME_OPTS]
  return (
    <div key={feat.id}>
      <div className="expand-grid">
        <div className="eg"><label>Feature Name</label><input defaultValue={feat.title} onBlur={(e) => set('title', e.target.value)} /></div>
        <div className="eg" />
      </div>
      <div className="expand-grid">
        <div className="eg"><label>Customer(s)</label><select value={custVal} onChange={(e) => set('cust', e.target.value)}>{CUST_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select></div>
        <div className="eg"><label>Priority</label><select value={feat.pri} onChange={(e) => set('pri', e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
        <div className="eg"><label>Module *</label><select value={feat.module} onChange={(e) => set('module', e.target.value)}><option value="">Select a module…</option>{MODULE_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
      </div>
      <label className="rq-design" style={{ margin: '2px 0 10px' }}><input type="checkbox" checked={!!feat.isBug} onChange={(e) => set('isBug', e.target.checked)} /> Mark as a bug</label>
      <div className="expand-grid">
        <div className="eg"><label>Date Added</label><input type="date" value={feat.dateAdded || ''} onChange={(e) => set('dateAdded', e.target.value)} /><span className="eg-hint">When the request entered the board (auto-set on creation).</span></div>
        <div className="eg" />
      </div>
      <div className="expand-grid">
        <div className="eg"><label>Assigned Dev</label><select value={feat.dev} onChange={(e) => set('dev', e.target.value)}><option value="">Unassigned</option>{DEVCOLS.map((k) => <option key={k} value={k}>{DL[k]}</option>)}</select></div>
        <div className="eg"><label>Status / Column</label><select value={feat.status} onChange={(e) => mutate((fs) => (POOLCOLS.indexOf(e.target.value) > -1 ? M.moveToBand(fs, feat.id, e.target.value) : M.moveStatus(fs, feat.id, e.target.value)))}>{Object.keys(SL).map((k) => <option key={k} value={k}>{SL[k]}</option>)}</select></div>
      </div>
      {feat.status !== 'live' && feat.status !== 'needsreview' && feat.status !== 'archived' && (
        <div className="expand-grid">
          <div className="eg"><label>Priority Position <span className="eg-hint">(within its band)</span></label><select value={myRank || 1} onChange={(e) => mutate((fs) => M.moveToPosition(fs, feat.id, parseInt(e.target.value, 10)))}>{bandPositions.map((i) => <option key={i} value={i}>{i}</option>)}</select></div>
          <div className="eg" />
        </div>
      )}
      <div className="dz-group">
        <div className="dz-hd">Design</div>
        <label className="rq-design"><input type="checkbox" checked={!!feat.requiresDesign} onChange={(e) => mutate((fs) => M.setRequiresDesign(fs, feat.id, e.target.checked))} /> Design required (untick = can’t enter design columns)</label>
        {isDesignStage && feat.requiresDesign && (
          <>
            <div className="expand-grid" style={{ marginTop: 10 }}>
              <div className="eg"><label>Design Deadline</label><input type="date" value={feat.designDeadline || ''} onChange={(e) => set('designDeadline', e.target.value)} /></div>
              <div className="eg"><label>Design Started</label><input type="date" value={feat.designStart || ''} onChange={(e) => set('designStart', e.target.value)} /><span className="eg-hint">Auto-set when it enters Design in Progress.</span></div>
            </div>
            {feat.status === 'design' && (
              <div className="eg" style={{ marginTop: 10 }}>
                <label>Design Progress: {feat.designProgress || 0}%</label>
                <input type="range" min={0} max={100} step={5} defaultValue={feat.designProgress || 0} onMouseUp={(e) => mutate((fs) => M.updDesignProgress(fs, feat.id, (e.target as HTMLInputElement).value))} onTouchEnd={(e) => mutate((fs) => M.updDesignProgress(fs, feat.id, (e.target as HTMLInputElement).value))} />
              </div>
            )}
            <div className="eg" style={{ marginTop: 10 }}><label>Design Notes</label><textarea placeholder="Design progress / decisions..." defaultValue={feat.designNotes || ''} onBlur={(e) => set('designNotes', e.target.value)} /></div>
            {feat.status === 'designcomplete' && (
              <div className="eg" style={{ marginTop: 10 }}><label>Design Completed</label><input type="date" value={feat.designCompletedOn || ''} onChange={(e) => set('designCompletedOn', e.target.value)} /><span className="eg-hint">Auto-set when it reaches Design Complete.</span></div>
            )}
          </>
        )}
      </div>
      <div className="expand-grid">
        <div className="eg">
          <label>Estimated Development Time</label>
          {feat.multiStage
            ? <div className="dv-d-derivednote">Derived from the stages ({feat.time ? timeLabel(feat.time) : 'TBD'}) — edit the stage estimates instead.</div>
            : <select value={feat.time} onChange={(e) => mutate((fs) => M.updTime(fs, feat.id, e.target.value))}>{timeOpts.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}</select>}
          {feat.time && <span className="eg-hint">Effort: {EL[feat.eff]}</span>}
        </div>
        <div className="eg"><label>Deadline</label><input type="date" value={feat.deadline || ''} min={feat.start || undefined} onChange={(e) => mutate((fs) => M.setDate(fs, feat.id, 'deadline', e.target.value))} /></div>
      </div>
      {feat.status === 'live' && (
        <div className="expand-grid">
          <div className="eg"><label>Actual Completion Date</label><input type="date" value={feat.completedOn || ''} onChange={(e) => set('completedOn', e.target.value)} /><span className="eg-hint">Auto-set the day it enters Live; clears if moved back.</span></div>
          <div className="eg" />
        </div>
      )}
      {prog ? (
        <>
          <div className="expand-grid">
            <div className="eg">
              <label>Progress: {feat.progress || 0}%</label>
              <input type="range" min={0} max={100} step={5} defaultValue={feat.progress || 0} onMouseUp={(e) => mutate((fs) => M.updProgress(fs, feat.id, (e.target as HTMLInputElement).value))} onTouchEnd={(e) => mutate((fs) => M.updProgress(fs, feat.id, (e.target as HTMLInputElement).value))} />
            </div>
            <div className="eg"><label>Start Date</label><input type="date" value={feat.start || ''} max={feat.deadline || undefined} onChange={(e) => mutate((fs) => M.setDate(fs, feat.id, 'start', e.target.value))} /></div>
          </div>
          <div className="eg" style={{ marginBottom: 10 }}><label>Progress Notes</label><textarea placeholder="Dev progress / blockers..." defaultValue={feat.pnotes} onBlur={(e) => set('pnotes', e.target.value)} /></div>
        </>
      ) : (
        <div className="expand-grid">
          <div className="eg"><label>Start Date</label><input type="date" value={feat.start || ''} max={feat.deadline || undefined} onChange={(e) => mutate((fs) => M.setDate(fs, feat.id, 'start', e.target.value))} /></div>
          <div className="eg" />
        </div>
      )}
      <div className="eg" style={{ marginBottom: 10 }}><label>Attachments / Links</label><LinksEditor feat={feat} /></div>
      <div className="eg"><label>Notes</label><textarea defaultValue={feat.notes} onBlur={(e) => set('notes', e.target.value)} /></div>
      <div className="dz-group" style={{ marginTop: 12 }}>
        <div className="dz-hd">Customer roadmap</div>
        <div className="expand-grid" style={{ marginTop: 8 }}>
          <div className="eg"><label>Customer-facing name</label><input defaultValue={feat.customerName || ''} placeholder="Blank = uses the feature name" onBlur={(e) => set('customerName', e.target.value)} /></div>
          <div className="eg" />
        </div>
        <div className="eg" style={{ marginTop: 8 }}><label>Customer-facing description</label><textarea placeholder="A brief, customer-friendly summary of what this delivers…" defaultValue={feat.customerDesc || ''} onBlur={(e) => set('customerDesc', e.target.value)} /></div>
        <label className="rq-design" style={{ marginTop: 10 }}><input type="checkbox" checked={feat.showOnRoadmap !== false} onChange={(e) => set('showOnRoadmap', e.target.checked)} /> Show on the customer roadmap</label>
      </div>
      {feat.status === 'onhold' ? (
        <div className="hold-actions"><button className="btn-resume" onClick={() => mutate((fs) => M.resumeCard(fs, feat.id))}>Resume (back to Dev column)</button></div>
      ) : pastDesign(feat.status) ? (
        <div className="hold-actions"><button className="btn-hold" onClick={() => mutate((fs) => M.placeOnHold(fs, feat.id))}>Place on Hold</button></div>
      ) : null}
      {feat.status === 'archived' ? (
        <div className="hold-actions"><button className="btn-resume" onClick={() => mutate((fs) => M.unarchive(fs, feat.id))}>Restore from archive</button></div>
      ) : (
        <div className="hold-actions"><button className="btn-archive" onClick={() => openSubModal({ kind: 'archive', id: feat.id })}><IcArchive size={13} /> Archive card</button></div>
      )}
    </div>
  )
}

export const EditView: React.FC<{ feat: Feature; onToggleEdit?: () => void; onClose?: () => void }> = ({ feat, onToggleEdit, onClose }) => {
  const { toggleDetailEdit: ctxToggle, closeCardModal: ctxClose } = useDevBoard()
  const toggleDetailEdit = onToggleEdit || ctxToggle
  const closeCardModal = onClose || ctxClose
  return (
    <>
      <div className="dv-d-top">
        <div className="dv-d-heading"><h2 className="dv-d-title">Editing</h2></div>
        <div className="dv-d-actions"><button className="dv-edit-btn done" onClick={toggleDetailEdit}>✓ Done</button></div>
        <button className="dv-modal-close" title="Close" onClick={closeCardModal}>×</button>
      </div>
      <EditPanel feat={feat} />
    </>
  )
}

/* ── sub-modals ── */
const ARCHIVE_REASONS = ["Doesn't make sense", 'Not in the roadmap', "Wouldn't work for all users / disrupts current workflow too much", 'Other']

/* Hoisted OUT of SubModals (Lee, 28 Aug): defined inline it became a new
 * component type on every render, remounting the modal per keystroke and
 * dropping input focus after each letter. */
const ModalWrap: React.FC<{ w?: number; onClose: () => void; children: React.ReactNode }> = ({ w, onClose, children }) => (
  <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) onClose() }}>
    <div className="modal" style={w ? { width: w } : undefined}>{children}</div>
  </div>
)

const SubModals: React.FC = () => {
  const { subModal, openSubModal, cards, mutate, reqDatalists, closeCardModal, resolveMerge, addFeature } = useDevBoard()
  const [company, setCompany] = useState('')
  const [companyCrm, setCompanyCrm] = useState<{ id: number | string; kind: 'paid' | 'deal' } | null>(null)
  const [person, setPerson] = useState('')
  const [date, setDate] = useState(todayISO())
  const [reason, setReason] = useState(ARCHIVE_REASONS[0])
  const [other, setOther] = useState('')
  React.useEffect(() => { setCompany(''); setCompanyCrm(null); setPerson(''); setDate(todayISO()); setReason(ARCHIVE_REASONS[0]); setOther('') }, [subModal?.kind, (subModal as any)?.id])
  if (!subModal) return null
  const close = () => openSubModal(null)
  if (subModal.kind === 'suggestHelp') {
    return (
      <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) close() }}>
        <div className="modal" style={{ width: 520 }}>
          <h2>How the Suggested view works</h2>
          <div className="help-sec"><div className="help-hd">Two groups</div><p><b>Committed / scheduled</b> — cards where design is done or development has started. They stay in <b>roadmap order</b> at the top (earliest finish first). <b>Triage backlog</b> below is ordered by a <b>suggested score</b>.</p></div>
          <div className="help-sec"><div className="help-hd">The score (0–100, higher = higher priority)</div><p>Blends three things: <b>Priority</b> tag (40%), <b>Demand</b> — how many companies have asked (40%), and <b>Effort</b> — quick wins score higher (20%). If no demand is logged, it falls back to your priority tag so internal work isn’t penalised. Click any <span className="sug-chip" style={{ cursor: 'default' }}>Suggested 76</span> chip to see a card’s full breakdown.</p></div>
          <div className="help-sec"><div className="help-hd">Pinning &amp; blockers</div><p><b>Pin</b> a card to lock it at its current spot so it stays put even as other scores change; nudge it with the <b>up/down</b> arrows. <b>Mark as a blocker</b> forces a card to the very top. You can’t drag to reorder here — switch to <b>Sort: Priority</b> for hand ordering.</p></div>
          <div className="help-sec"><div className="help-hd">Saving</div><p><b>Apply suggested layout</b> writes this order into your priority list (with an Undo). <b>Remove all pinned and reset suggestions</b> clears every pin and blocker.</p></div>
          <div className="factions"><button className="btn-p" onClick={close}>Got it</button></div>
        </div>
      </div>
    )
  }
  if (subModal.kind === 'addFeature') {
    return <AddFeatureModal onClose={close} />
  }
  if (subModal.kind === 'resetSuggest') {
    return (
      <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) close() }}>
        <div className="modal" style={{ width: 440 }}>
          <h2>Reset suggestions</h2>
          <div className="modal-sub">Remove all pins and blockers and reset the suggested layout?</div>
          <div className="factions"><button className="btn-s" onClick={close}>Cancel</button><button className="btn-p" onClick={() => { mutate((fs) => S.resetSuggestions(fs)); close() }}>Reset</button></div>
        </div>
      </div>
    )
  }
  const feat = cards.find((f) => f.id === (subModal as any).id)
  if (!feat) return null
  if (subModal.kind === 'schedule') {
    return (
      <ModalWrap w={feat.multiStage ? 560 : 700} onClose={close}>
        <ScheduleModalBody feat={feat} onClose={close} />
      </ModalWrap>
    )
  }
  if (subModal.kind === 'addRequest') {
    return (
      <ModalWrap w={420} onClose={close}>
        <h2>Add a request / nudge</h2>
        <div className="modal-sub">For: {feat.title}</div>
        <div className="fg"><label>Company *</label>
          <CustomerPicker value={company} boardCompanies={reqDatalists.companies} placeholder="e.g. Avenue Systems" pickedCrm={companyCrm}
            onChange={(v) => { setCompany(v); setCompanyCrm(null) }}
            onPick={(pk) => { setCompany(pk.name); setCompanyCrm(pk.crmId != null ? { id: pk.crmId, kind: pk.crmKind! } : null) }} />
        </div>
        <div className="fg"><label>Requested by</label><input list="wqPersonList" value={person} onChange={(e) => setPerson(e.target.value)} placeholder="Person's name" /><datalist id="wqPersonList">{reqDatalists.people.map((p) => <option key={p} value={p} />)}</datalist></div>
        <div className="fg"><label>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="factions"><button className="btn-s" onClick={close}>Cancel</button><button className="btn-p" onClick={() => { mutate((fs) => M.addRequest(fs, feat.id, company, person, date, companyCrm || undefined)); if (company.trim()) close() }}>Log request</button></div>
      </ModalWrap>
    )
  }
  if (subModal.kind === 'chaseLog') {
    return (
      <ModalWrap w={380} onClose={close}>
        <h2>Log a chase</h2>
        <div className="modal-sub">Chase from <strong>{subModal.company || 'Unknown company'}</strong>{subModal.person ? ' · ' + subModal.person : ''}</div>
        <div className="fg"><label>Chase date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div className="factions"><button className="btn-s" onClick={close}>Cancel</button><button className="btn-p" onClick={() => { mutate((fs) => M.logChaseAppend(fs, feat.id, subModal.company, subModal.person, date)); close() }}>Log chase</button></div>
      </ModalWrap>
    )
  }
  if (subModal.kind === 'chaseHistory') {
    const g = reqGroups(feat).find((x) => x.company === subModal.company && x.person === subModal.person)
    const dates = g ? g.dates.slice().sort() : []
    return (
      <ModalWrap w={400} onClose={close}>
        <h2>Chase history</h2>
        <div className="req-chase-sub">{subModal.person || 'Unknown'} · {subModal.company || ''}</div>
        <div className="req-chase-list">
          <div className="req-chase-item"><span>Original request</span><span>{dates[0] ? fmtDate(dates[0]) : '—'}</span></div>
          {dates.slice(1).map((d, i) => <div key={i} className="req-chase-item"><span>Chased</span><span>{fmtDate(d)}</span></div>)}
        </div>
        <div className="factions"><button className="btn-s" onClick={close}>Close</button></div>
      </ModalWrap>
    )
  }
  if (subModal.kind === 'markLive') {
    return (
      <ModalWrap w={420} onClose={close}>
        <h2>Mark as Live</h2>
        <div className="modal-sub">Mark “{feat.title}” as Live? It’ll be marked shipped with today’s date.</div>
        <div className="factions"><button className="btn-s" onClick={close}>Cancel</button><button className="btn-p" onClick={() => { mutate((fs) => M.markLive(fs, feat.id)); close() }}>Mark as Live</button></div>
      </ModalWrap>
    )
  }
  if (subModal.kind === 'merge') {
    const scored = R.similarScored(feat, cards)
    const suggested = scored.filter((x) => x.sim > 0).slice(0, 5)
    const others = scored.filter((x) => x.sim <= 0)
    const Cand = (c: Feature) => (
      <button key={c.id} className="merge-cand" onClick={() => resolveMerge(feat.id, c.id)}>
        <span className="merge-cand-title">{c.title}</span>
        <span className="merge-cand-sub">{statusLabels()[c.status] || c.status} · requested by {reqCount(c)} compan{reqCount(c) === 1 ? 'y' : 'ies'}</span>
      </button>
    )
    return (
      <ModalWrap w={560} onClose={close}>
        <h2>Merge into an existing card</h2>
        <div className="modal-sub">Merging <strong>“{feat.title}”</strong>. Pick the existing card it duplicates — its requester joins that card’s request count, and this review item is removed.</div>
        {suggested.length > 0 && <><div className="merge-grp-h">Suggested matches</div>{suggested.map((x) => Cand(x.feat))}</>}
        <div className="merge-grp-h">All other cards</div>
        <div className="merge-scroll">{others.length ? others.map((x) => Cand(x.feat)) : <div className="cmt-empty">No other cards.</div>}</div>
        <div className="factions"><button className="btn-s" onClick={close}>Cancel</button></div>
      </ModalWrap>
    )
  }
  if (subModal.kind === 'pinConfirm') {
    return (
      <ModalWrap w={460} onClose={close}>
        <h2>Pin this position?</h2>
        <div className="modal-sub">Pinning keeps <strong>{feat.title}</strong> at position #{subModal.pos + 1} even as other cards’ scores change. Use the up/down arrows to move it, or unpin any time.</div>
        <div className="factions"><button className="btn-s" onClick={close}>Cancel</button><button className="btn-p" onClick={() => { mutate((fs) => S.confirmPin(fs, feat.id, subModal.pos)); close() }}>Pin it</button></div>
      </ModalWrap>
    )
  }
  if (subModal.kind === 'suggestInfo') {
    const sp = S.suggestTriagePos(feat, cards)
    const cur = S.currentTriagePos(feat, cards)
    const pPts = Math.round(S.SUG_PRI_W * S.priScoreV(feat) * 100)
    const dPts = Math.round(S.SUG_DEM_W * S.demScoreV(feat) * 100)
    const ePts = Math.round(S.SUG_EFF_W * S.effScoreV(feat) * 100)
    const c = reqCount(feat)
    const demTxt = c > 0 ? c + ' compan' + (c === 1 ? 'y' : 'ies') + ' asking' : 'no demand logged — uses your priority tag'
    const priLbl = feat.pri === 'high' ? 'High' : feat.pri === 'medium' ? 'Medium' : 'Low'
    const effLbl = feat.eff === 'quickwin' ? 'quick win' : feat.eff === 'low' ? 'low effort' : feat.eff === 'medium' ? 'medium effort' : 'big job'
    const move = cur === sp.pos ? 'It’s already in its suggested spot.' : sp.pos < cur ? 'Suggests moving it up ' + (cur - sp.pos) + ' place' + (cur - sp.pos === 1 ? '' : 's') + '.' : 'Suggests moving it down ' + (sp.pos - cur) + ' place' + (sp.pos - cur === 1 ? '' : 's') + '.'
    return (
      <ModalWrap w={480} onClose={close}>
        <h2>Suggested priority</h2>
        <div className="modal-sub">{feat.title}</div>
        <div className="sug-info-score"><span className="sug-info-num">{S.suggestScore(feat)}</span><span className="sug-info-of">/ 100</span><span className="sug-info-hint">higher = higher priority</span></div>
        <div className="sug-info-pos"><b>Where it would sit:</b> #{sp.pos} of {sp.total} in the triage backlog<br /><span style={{ color: 'var(--g600)' }}>Currently #{cur}. {move}</span></div>
        <div className="sug-info-why">
          <div className="sug-info-hd">How the score is made up</div>
          <div className="sug-info-row"><span>Priority · {priLbl}</span><span>{pPts} / 40</span></div>
          <div className="sug-info-row"><span>Demand · {demTxt}</span><span>{dPts} / 40</span></div>
          <div className="sug-info-row"><span>Effort · {effLbl}</span><span>{ePts} / 20</span></div>
        </div>
        {feat.blocker && <div className="sug-info-note"><IcFlag size={11} /> Marked as a blocker — pinned to the top regardless of score.</div>}
        {feat.triagePinned && <div className="sug-info-note"><IcPin size={11} /> Pinned to a fixed position; unpin to let it flow by score.</div>}
        <div className="factions"><button className="btn-s" onClick={close}>Close</button></div>
      </ModalWrap>
    )
  }
  /* archive */
  return (
    <ModalWrap w={440} onClose={close}>
      <h2>Archive card</h2>
      <div className="modal-sub">Archiving: {feat.title}</div>
      <div className="fg"><label>Reason *</label>
        <select value={reason} onChange={(e) => setReason(e.target.value)}>{ARCHIVE_REASONS.map((r) => <option key={r} value={r}>{r === 'Other' ? 'Other (type your own)' : r}</option>)}</select>
      </div>
      {reason === 'Other' && <div className="fg"><label>Your reason</label><textarea value={other} onChange={(e) => setOther(e.target.value)} placeholder="Type the reason..." /></div>}
      <div className="factions"><button className="btn-s" onClick={close}>Cancel</button><button className="btn-p" onClick={() => { const r = reason === 'Other' ? other : reason; mutate((fs) => M.confirmArchive(fs, feat.id, r)); if (r.trim()) { close(); closeCardModal() } }}>Archive</button></div>
    </ModalWrap>
  )
}

/* ── the modal shell ── */
/** Needs-Review pop-up variant (reviewModalHtml): resolve bar top-right. */
/* ReviewModalBody removed (Lee, 27 Aug): Needs Review cards now use the
   standard ReadView with the resolve bar — edit only via the pencil. */

/** Archived pop-up variant (syncCardModal): checklist + edit/restore form. */
const ArchivedModalBody: React.FC<{ feat: Feature }> = ({ feat }) => {
  const { closeCardModal } = useDevBoard()
  return (
    <>
      <div className="dv-d-top">
        <div className="dv-d-heading"><span className="rv-tag arch">ARCHIVED</span><h2 className="dv-d-title">{feat.title}</h2></div>
        <button className="dv-modal-close" title="Close" onClick={closeCardModal}>×</button>
      </div>
      <div className="dv-d-pills"><span className={'badge priority-' + feat.pri}>{PL[feat.pri]}</span><span className="status-pill s-archived">Archived</span></div>
      <ChecklistBlock feat={feat} />
      <EditPanel feat={feat} />
    </>
  )
}

export const CardModal: React.FC = () => {
  const { cards, modalCardId, detailEditMode, closeCardModal } = useDevBoard()
  const feat = modalCardId != null ? cards.find((f) => f.id === modalCardId) || null : null
  if (!feat) return <SubModals />
  /* Lee, 27 Aug: Needs Review cards open in the NORMAL read view (with the
   * resolve bar + inline quick-edit chips) — edit mode is the pencil's job. */
  const body = feat.status === 'archived'
    ? <ArchivedModalBody feat={feat} />
    : detailEditMode ? <EditView feat={feat} /> : <ReadView feat={feat} />
  return (
    <>
      <div className="overlay open" onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) closeCardModal() }}>
        <div className="modal modal-card">{body}</div>
      </div>
      <SubModals />
    </>
  )
}

/* ── Add Feature (addFeature form → Needs Review) ── */
const AddFeatureModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { addFeature, reqDatalists } = useDevBoard()
  const [f, setF] = useState<R.AddFeatureFields>({ title: '', cust: '', pri: 'medium', time: '', deadline: '', designDeadline: '', start: '', notes: '', requiresDesign: true, reqCompany: '', reqPerson: '', module: '', isBug: false })
  const up = (k: keyof R.AddFeatureFields, v: any) => setF((s0) => ({ ...s0, [k]: v }))
  return (
    <div className="overlay open" style={{ zIndex: 130 }} onClick={(e) => { if ((e.target as HTMLElement).classList.contains('overlay')) onClose() }}>
      <div className="modal" style={{ width: 520 }}>
        <h2>Add New Feature</h2>
        <div className="modal-sub">New features enter <strong>Needs Review</strong> first — add to the dev list, merge or archive from there.</div>
        <div className="fg"><label>Feature Name *</label><input value={f.title} onChange={(e) => up('title', e.target.value)} placeholder="e.g. PDF export for quotes" /></div>
        <div className="frow">
          <div className="fg"><label>Customer(s)</label><select value={f.cust} onChange={(e) => up('cust', e.target.value)}>{CUST_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}</select></div>
          <div className="fg"><label>Priority</label><select value={f.pri} onChange={(e) => up('pri', e.target.value)}><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
          <div className="fg"><label>Module *</label><select value={f.module} onChange={(e) => up('module', e.target.value)}><option value="">Select a module…</option>{MODULE_OPTS.map((m) => <option key={m} value={m}>{m}</option>)}</select></div>
          <div className="fg"><label>Flags</label><div className="fg-ticks">
            <label className="rq-design"><input type="checkbox" checked={f.requiresDesign} onChange={(e) => up('requiresDesign', e.target.checked)} /> Design required</label>
            <label className="rq-design"><input type="checkbox" checked={f.isBug} onChange={(e) => up('isBug', e.target.checked)} /> Bug</label>
          </div></div>
        </div>
        <div className="frow">
          <div className="fg"><label>Requesting company</label>
            <CustomerPicker value={f.reqCompany} boardCompanies={reqDatalists.companies} placeholder="Who asked? (optional)" pickedCrm={f.reqCrmId != null ? { id: f.reqCrmId, kind: f.reqCrmKind! } : null}
              onChange={(v) => setF((s0) => ({ ...s0, reqCompany: v, reqCrmId: undefined, reqCrmKind: undefined }))}
              onPick={(pk) => setF((s0) => ({ ...s0, reqCompany: pk.name, reqCrmId: pk.crmId, reqCrmKind: pk.crmKind }))} />
          </div>
          <div className="fg"><label>Requester name</label><input value={f.reqPerson} onChange={(e) => up('reqPerson', e.target.value)} placeholder="Person (optional)" /></div>
        </div>
        <div className="frow">
          <div className="fg">
            <label>Estimated Development Time</label>
            <select value={f.time} onChange={(e) => up('time', e.target.value)}>{TIME_OPTS.map((o) => <option key={o.val} value={o.val}>{o.label}</option>)}</select>
            <span className="eg-hint">{(() => { const t = calcEffortLocal(f.time); return t ? 'Effort auto-sets: ' + EL[t] : '' })()}</span>
          </div>
          <div className="fg"><label>Deadline</label><input type="date" value={f.deadline} onChange={(e) => up('deadline', e.target.value)} /></div>
        </div>
        <div className="frow">
          <div className="fg"><label>Design Deadline</label><input type="date" value={f.designDeadline} onChange={(e) => up('designDeadline', e.target.value)} /></div>
          <div className="fg"><label>Start Date</label><input type="date" value={f.start} onChange={(e) => up('start', e.target.value)} /></div>
        </div>
        <div className="fg"><label>Notes / Context</label><textarea value={f.notes} onChange={(e) => up('notes', e.target.value)} placeholder="Dependencies, who requested it..." /></div>
        <div className="factions">
          <button className="btn-s" onClick={onClose}>Cancel</button>
          <button className="btn-p" onClick={() => { void addFeature(f) }}>Add Feature</button>
        </div>
      </div>
    </div>
  )
}
